import { useEffect, useMemo, useState } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import {
  Users, Search, FileText, Plus, Trash2, ExternalLink, Copy, Ruler, CalendarDays, Mail, Phone, MapPin, MessageSquare, Key, Send, Save,
  Clock, CreditCard, Percent, ChevronRight, History
} from "lucide-react";

interface ClientRecord {
  id: string;
  full_name: string;
  assigned_staff?: string[]; // New field for multiple professionals
  email: string;
  phone: string;
  birth_date: string | null;
  address: string | null;
  profession: string | null;
  emergency_name: string | null;
  emergency_phone: string | null;
  health_conditions: string[] | null;
  allergies: string | null;
  oil_allergies: string | null;
  avoid_areas: string | null;
  treatment_types: string[] | null;
  pressure_preference: string | null;
  likes_essential_oils: boolean | null;
  aroma_consent: boolean | null;
  aroma_preference: string | null;
  medications: string | null;
  surgeries: string | null;
  is_pregnant: boolean;
  pregnancy_weeks: number | null;
  goals: string | null;
  observations: string | null;
  consent_accepted: boolean;
  signature_data: string | null;
  signed_at: string | null;
  created_at: string;
}

interface ClientSession {
  id: string;
  record_id: string;
  session_date: string;
  staff_name: string | null;
  service_performed: string | null;
  products_used: string | null;
  notes: string | null;
  weight: number | null;
  height: number | null;
  measurements: Record<string, number | string> | null;
  created_at: string;
}

const MEASURE_FIELDS: { key: string; label: string }[] = [
  { key: "peito", label: "Peito (cm)" },
  { key: "cintura", label: "Cintura (cm)" },
  { key: "abdomen", label: "Abdómen (cm)" },
  { key: "anca", label: "Anca (cm)" },
  { key: "braco_esq", label: "Braço esq. (cm)" },
  { key: "braco_dir", label: "Braço dir. (cm)" },
  { key: "coxa_esq", label: "Coxa esq. (cm)" },
  { key: "coxa_dir", label: "Coxa dir. (cm)" },
  { key: "gemeo_esq", label: "Gémeo esq. (cm)" },
  { key: "gemeo_dir", label: "Gémeo dir. (cm)" },
];

const emptySession = {
  session_date: new Date().toISOString().slice(0, 10),
  service_performed: "",
  products_used: "",
  notes: "",
  weight: "",
  height: "",
};

const ClientsAdmin = () => {
  const { user } = useAuth();
  const [records, setRecords] = useState<ClientRecord[]>([]);
  const [staffList, setStaffList] = useState<{ id: string, name: string }[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState<ClientRecord | null>(null);
  const [sessions, setSessions] = useState<ClientSession[]>([]);
  const [clientBookings, setClientBookings] = useState<any[]>([]);
  const [loadingHistory, setLoadingHistory] = useState(false);
  const [sessionOpen, setSessionOpen] = useState(false);
  const [sessionForm, setSessionForm] = useState(emptySession);
  const [measures, setMeasures] = useState<Record<string, string>>({});
  const [savingSession, setSavingSession] = useState(false);
  const [resending, setResending] = useState(false);
  const [updatingAuth, setUpdatingAuth] = useState(false);

  const fetchClientHistoryByContact = async (email: string, phone: string) => {
    setLoadingHistory(true);
    try {
      const { data: bookingData } = await supabase
        .from("bookings")
        .select("*, services(name)")
        .or(`client_email.ilike."${email}",client_phone.eq."${phone}"`)
        .order("booking_date", { ascending: false })
        .order("booking_time", { ascending: false });
      setClientBookings(bookingData || []);
    } catch (err) {
      console.error("Error fetching history by contact:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  const fetchStaff = async () => {
    const { data } = await supabase.from("staff").select("id, name").eq("is_active", true);
    if (data) setStaffList(data);
  };

  const fetchRecords = async () => {
    const { data: userRoles } = await supabase
      .from("user_roles")
      .select("role")
      .eq("user_id", user?.id)
      .maybeSingle();

    const isAdmin = userRoles?.role === 'admin';

    let query = supabase.from("client_records").select("*");

    if (!isAdmin && user?.user_metadata?.full_name) {
      // Filter records assigned to this staff member
      const staffName = user.user_metadata.full_name;
      query = query.contains("assigned_staff", [staffName]);
    }

    const { data, error } = await query.order("created_at", { ascending: false });

    if (error) toast.error("Erro ao carregar fichas.");
    setRecords((data as unknown as ClientRecord[]) || []);
    setLoading(false);
  };

  const fetchClientHistory = async (record: ClientRecord) => {
    setLoadingHistory(true);
    try {
      // Fetch Evaluation Sessions
      const { data: sessionData } = await supabase
        .from("client_sessions")
        .select("*")
        .eq("record_id", record.id)
        .order("session_date", { ascending: false });
      setSessions((sessionData as unknown as ClientSession[]) || []);

      // Fetch Bookings (matching by record_id OR email OR phone)
      const { data: bookingData } = await supabase
        .from("bookings")
        .select("*, services(name)")
        .or(`client_record_id.eq."${record.id}",client_email.ilike."${record.email}",client_phone.eq."${record.phone}"`)
        .order("booking_date", { ascending: false })
        .order("booking_time", { ascending: false });
      setClientBookings(bookingData || []);
    } catch (err) {
      console.error("Error fetching history:", err);
    } finally {
      setLoadingHistory(false);
    }
  };

  useEffect(() => { 
    fetchRecords(); 
    fetchStaff();

    // Check for search param from finance page
    const params = new URLSearchParams(window.location.search);
    const searchQuery = params.get("search");
    const searchEmail = params.get("email");
    const searchPhone = params.get("phone");

    if (searchQuery) {
      setSearch(searchQuery);
    }

    if (searchEmail || searchPhone) {
      // If we only have email/phone but no client record is found yet, 
      // we still want to show bookings for that person
      fetchClientHistoryByContact(searchEmail || "", searchPhone || "");
    }
  }, []);

  const openRecord = async (r: ClientRecord) => {
    setSelected(JSON.parse(JSON.stringify(r))); // Clone to avoid direct mutations
    setSessions([]);
    setClientBookings([]);
    // We pass r instead of selected because state might not have updated yet
    setTimeout(() => fetchClientHistory(r), 10);
  };

  const [timeFilter, setTimeFilter] = useState("all");
  const [staffSearch, setStaffSearch] = useState("");

  const filtered = useMemo(() => {
    let result = records;
    const q = search.trim().toLowerCase();
    const s = staffSearch.trim().toLowerCase();

    if (q) {
      result = result.filter(
        (r) =>
          r.full_name.toLowerCase().includes(q) ||
          r.email.toLowerCase().includes(q) ||
          r.phone.toLowerCase().includes(q),
      );
    }

    if (s) {
      result = result.filter((r) => 
        (r.assigned_staff || []).some(name => name.toLowerCase().includes(s))
      );
    }

    if (timeFilter !== "all") {
      const now = new Date();
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      
      const startOfWeek = new Date(today);
      startOfWeek.setDate(today.getDate() - today.getDay()); // Sunday

      const startOfMonth = new Date(now.getFullYear(), now.getMonth(), 1);
      
      result = result.filter((r) => {
        const d = new Date(r.created_at);
        if (timeFilter === "today") return d >= today;
        if (timeFilter === "week") return d >= startOfWeek;
        if (timeFilter === "month") return d >= startOfMonth;
        return true;
      });
    }

    return result;
  }, [records, search, staffSearch, timeFilter]);

  // Automatically open record if there's only one result from a search-param redirect
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    if (params.get("search") && filtered.length === 1 && !selected) {
      openRecord(filtered[0]);
      // Clean up URL
      window.history.replaceState(null, "", window.location.pathname);
    } else if ((params.get("email") || params.get("phone")) && filtered.length === 0 && !selected) {
      // If no record found but we have email/phone, stay on page but show empty record state or just keep the search results as they are
      // We already fetched history in the mount useEffect
    } else if ((params.get("email") || params.get("phone")) && filtered.length === 1 && !selected) {
      openRecord(filtered[0]);
      window.history.replaceState(null, "", window.location.pathname);
    }
  }, [filtered, selected]);

  const deleteRecord = async (id: string) => {
    if (!confirm("Apagar esta ficha e todo o histórico de sessões?")) return;
    const { error } = await supabase.from("client_records").delete().eq("id", id);
    if (error) return toast.error("Não foi possível apagar.");
    toast.success("Ficha apagada.");
    setSelected(null);
    fetchRecords();
  };

  const openNewSession = () => {
    setSessionForm(emptySession);
    setMeasures({});
    setSessionOpen(true);
  };

  const saveSession = async () => {
    if (!selected) return;
    setSavingSession(true);
    const cleanMeasures: Record<string, number> = {};
    Object.entries(measures).forEach(([k, v]) => {
      if (v !== "" && !isNaN(Number(v))) cleanMeasures[k] = Number(v);
    });
    const { error } = await supabase.from("client_sessions").insert({
      record_id: selected.id,
      session_date: sessionForm.session_date,
      staff_name: user?.user_metadata?.full_name || user?.email || "Equipa",
      service_performed: sessionForm.service_performed.trim().slice(0, 300) || null,
      products_used: sessionForm.products_used.trim().slice(0, 500) || null,
      notes: sessionForm.notes.trim().slice(0, 2000) || null,
      weight: sessionForm.weight ? Number(sessionForm.weight) : null,
      height: sessionForm.height ? Number(sessionForm.height) : null,
      measurements: cleanMeasures,
    });
    setSavingSession(false);
    if (error) return toast.error("Erro ao guardar a sessão.");
    toast.success("Sessão registada.");
    setSessionOpen(false);
    fetchClientHistory(selected);
  };

  const deleteSession = async (id: string) => {
    if (!confirm("Apagar este registo de sessão?")) return;
    await supabase.from("client_sessions").delete().eq("id", id);
    if (selected) fetchClientHistory(selected);
  };

  const copyFormLink = () => {
    navigator.clipboard.writeText(`${window.location.origin}/ficha`);
    toast.success("Link da ficha copiado!");
  };

  const resendStaffNotification = async (client: ClientRecord) => {
    setResending(true);
    try {
      // Notify all assigned staff members
      const staffToNotify = client.assigned_staff && client.assigned_staff.length > 0 
        ? client.assigned_staff 
        : ["Thaiane"];

      for (const staffName of staffToNotify) {
        const { error } = await supabase.functions.invoke("notify-staff-client-record", {
          body: {
            client_name: client.full_name,
            client_email: client.email,
            client_phone: client.phone,
            staff_name_selection: staffName,
            record_data: {
              health_conditions: client.health_conditions,
              treatment_types: client.treatment_types,
              pressure_preference: client.pressure_preference,
              aroma_consent: client.aroma_consent,
            },
          },
        });
        if (error) throw error;
      }
      toast.success("Notificação enviada ao(s) colaborador(es)!");
    } catch (e: any) {
      console.error("Resend error:", e);
      toast.error("Erro ao reenviar notificação: " + (e.message || "Tente novamente"));
    } finally {
      setResending(false);
    }
  };

  const sendPasswordReset = async (email: string) => {
    if (!email) return;
    setUpdatingAuth(true);
    const PROD_ORIGIN = "https://mentesa.pt";
    const origin = window.location.hostname === "mentesa.pt" || window.location.hostname === "www.mentesa.pt"
      ? window.location.origin
      : PROD_ORIGIN;
    
    const { error } = await supabase.auth.resetPasswordForEmail(email, {
      redirectTo: `${origin}/reset-password`,
    });
    setUpdatingAuth(false);
    if (error) {
      toast.error("Erro ao enviar reset: " + error.message);
    } else {
      toast.success("Email de redefinição enviado!");
    }
  };

  const updateClientPassword = async (email: string) => {
    const newPass = prompt("Introduza a nova palavra-passe para este cliente (mín. 6 caracteres):");
    if (!newPass) return;
    if (newPass.length < 6) return toast.error("A palavra-passe deve ter pelo menos 6 caracteres.");

    setUpdatingAuth(true);
    // Note: This requires admin privileges (service role) which the client SDK doesn't have.
    // However, if the user is currently logged in as this client, it would work.
    // For a true admin "force change", we usually use an Edge Function.
    // Let's try the standard way first, but clarify it might need the Edge Function for cross-user updates.
    const { data: res, error } = await (supabase.rpc as any)('admin_update_user_password', { 
      target_email: email, 
      new_password: newPass 
    });
    
    setUpdatingAuth(false);
    if (res?.error) {
      toast.error("Erro: " + res.error);
    } else if (error) {
      console.error("Password update error:", error);
      toast.error("Erro ao atualizar: " + error.message);
    } else {
      toast.success("Palavra-passe atualizada com sucesso!");
    }
  };

  const [addDialogOpen, setAddDialogOpen] = useState(false);
  const [newClient, setNewClient] = useState({ full_name: "", email: "", phone: "" });
  const [addingClient, setAddingClient] = useState(false);

  const handleAddClient = async () => {
    if (!newClient.full_name || !newClient.email || !newClient.phone) {
      return toast.error("Preencha o nome, email e telemóvel.");
    }
    setAddingClient(true);
    
    // Check if already exists
    const { data: existing } = await supabase
      .from("client_records")
      .select("id")
      .or(`email.eq.${newClient.email.trim().toLowerCase()},phone.eq.${newClient.phone.trim()}`)
      .maybeSingle();

    if (existing) {
      setAddingClient(false);
      return toast.error("Já existe um cliente com este email ou telemóvel.");
    }

    const { error } = await supabase.from("client_records").insert({
      full_name: newClient.full_name.trim(),
      email: newClient.email.trim().toLowerCase(),
      phone: newClient.phone.trim(),
      health_conditions: [],
      treatment_types: [],
      consent_accepted: false,
      likes_essential_oils: false,
      aroma_consent: false,
      is_pregnant: false,
    });

    setAddingClient(false);
    if (error) return toast.error("Erro ao criar cliente.");
    
    toast.success("Cliente criado com sucesso.");
    setAddDialogOpen(false);
    setNewClient({ full_name: "", email: "", phone: "" });
    fetchRecords();
  };

  return (
    <div className="space-y-6">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="font-display text-2xl font-bold flex items-center gap-2">
            <Users size={22} className="text-primary" /> Clientes
          </h1>
          <p className="text-sm text-muted-foreground">
            Gestão de clientes, fichas de avaliação e histórico de sessões.
          </p>
        </div>
        <div className="flex flex-wrap gap-2">
          <Button variant="outline" className="rounded-full" onClick={copyFormLink}>
            <Copy size={16} className="mr-2" /> Link da ficha
          </Button>
          <Button onClick={() => setAddDialogOpen(true)} className="rounded-full">
            <Plus size={16} className="mr-2" /> Novo Cliente
          </Button>
        </div>
      </div>

      <Dialog open={addDialogOpen} onOpenChange={setAddDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Registar Novo Cliente</DialogTitle>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Nome Completo</Label>
              <Input 
                value={newClient.full_name} 
                onChange={e => setNewClient({...newClient, full_name: e.target.value})} 
                placeholder="Ex: Maria Silva"
              />
            </div>
            <div className="space-y-2">
              <Label>Email</Label>
              <Input 
                type="email" 
                value={newClient.email} 
                onChange={e => setNewClient({...newClient, email: e.target.value})} 
                placeholder="cliente@email.com"
              />
            </div>
            <div className="space-y-2">
              <Label>Telemóvel</Label>
              <Input 
                value={newClient.phone} 
                onChange={e => setNewClient({...newClient, phone: e.target.value})} 
                placeholder="9XXXXXXXX"
              />
            </div>
          </div>
          <div className="flex justify-end gap-3">
            <Button variant="outline" onClick={() => setAddDialogOpen(false)}>Cancelar</Button>
            <Button onClick={handleAddClient} disabled={addingClient}>
              {addingClient ? "A criar..." : "Criar Cliente"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      <div className="flex flex-wrap gap-3">
        <div className="relative flex-1 min-w-full sm:min-w-[240px]">
          <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Cliente (nome, email, tel)..." className="pl-9" value={search} onChange={(e) => setSearch(e.target.value)} />
        </div>
        <div className="relative flex-1 min-w-full sm:min-w-[200px]">
          <Users size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
          <Input placeholder="Colaborador..." className="pl-9" value={staffSearch} onChange={(e) => setStaffSearch(e.target.value)} />
        </div>
        <Select value={timeFilter} onValueChange={setTimeFilter}>
          <SelectTrigger className="w-full sm:w-[140px]">
            <SelectValue placeholder="Filtrar data" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todos</SelectItem>
            <SelectItem value="today">Hoje</SelectItem>
            <SelectItem value="week">Esta Semana</SelectItem>
            <SelectItem value="month">Este Mês</SelectItem>
          </SelectContent>
        </Select>
      </div>

      {loading ? (
        <p className="text-muted-foreground">A carregar...</p>
      ) : (filtered.length === 0 && clientBookings.length === 0) ? (
        <div className="rounded-2xl border border-dashed border-border p-10 text-center text-muted-foreground">
          <FileText size={32} className="mx-auto mb-3 opacity-50" />
          Ainda não existem clientes registados. Pode registar um manualmente ou partilhar o link da ficha.
        </div>
      ) : (
        <div className="space-y-6">
          {filtered.length === 0 && clientBookings.length > 0 && (
            <div className="bg-amber-50 border border-amber-200 rounded-2xl p-6">
              <div className="flex items-center gap-3 mb-4">
                <History className="text-amber-600" size={24} />
                <h2 className="font-semibold text-amber-900">Histórico de Reservas Encontrado</h2>
              </div>
              <p className="text-amber-800 text-sm mb-4">
                Não existe uma ficha de avaliação para <strong>{search}</strong>, mas foram encontradas as seguintes reservas anteriores:
              </p>
              <div className="grid gap-3 md:grid-cols-2">
                {clientBookings.map((b) => (
                  <div key={b.id} className="bg-white border border-amber-100 rounded-xl p-4 shadow-sm">
                    <div className="flex justify-between items-start">
                      <div>
                        <p className="font-bold text-gray-900">{(b.services as any)?.name || "Serviço"}</p>
                        <p className="text-xs text-gray-500 mt-1">
                          {new Date(b.booking_date).toLocaleDateString("pt-PT")} às {b.booking_time.slice(0, 5)}
                        </p>
                      </div>
                      <Badge variant="outline" className="text-[10px] uppercase">
                        {b.status === "completed" ? "Concluída" : 
                         b.status === "confirmed" ? "Confirmada" : 
                         b.status === "pending" ? "Pendente" : b.status}
                      </Badge>
                    </div>
                  </div>
                ))}
              </div>
              <Button 
                variant="outline" 
                className="mt-4 border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => setAddDialogOpen(true)}
              >
                <Plus size={16} className="mr-2" /> Criar ficha agora
              </Button>
            </div>
          )}
          <div className="grid gap-3 md:grid-cols-2 xl:grid-cols-3">
            {filtered.map((r) => (
              <button
                key={r.id}
                onClick={() => openRecord(r)}
                className="text-left rounded-2xl border border-border bg-card p-4 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-2">
                  <p className="font-semibold">{r.full_name}</p>
                  {r.consent_accepted && <Badge variant="secondary">Assinada</Badge>}
                </div>
                <p className="text-xs text-muted-foreground mt-1 flex items-center gap-1"><Mail size={12} /> {r.email}</p>
                <p className="text-xs text-muted-foreground flex items-center gap-1"><Phone size={12} /> {r.phone}</p>
                <p className="text-[11px] text-muted-foreground mt-2">
                  Registada em {new Date(r.created_at).toLocaleDateString("pt-PT")}
                </p>
              </button>
            ))}
          </div>
        </div>
      )}

      {/* Detail dialog */}
      <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          {selected && (
            <>
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <FileText size={18} className="text-primary" /> {selected.full_name}
                </DialogTitle>
              </DialogHeader>

              <div className="space-y-5">
                <div className="flex flex-wrap justify-end gap-2 mb-2">
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-full text-xs h-8 bg-primary text-primary-foreground hover:bg-primary/90 border-primary"
                    onClick={async () => {
                      const { error } = await supabase.from("client_records").update(selected).eq("id", selected.id);
                      if (error) toast.error("Erro ao atualizar cliente.");
                      else {
                        toast.success("Cliente atualizado com sucesso!");
                        fetchRecords();
                      }
                    }}
                  >
                    <Save size={14} className="mr-1" /> Guardar Dados
                  </Button>
                  
                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-full text-xs h-8 text-amber-600 border-amber-200 hover:bg-amber-50"
                    onClick={() => sendPasswordReset(selected.email)}
                    disabled={updatingAuth}
                  >
                    <Send size={14} className="mr-1" /> Enviar Reset
                  </Button>

                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-full text-xs h-8 text-blue-600 border-blue-200 hover:bg-blue-50"
                    onClick={() => updateClientPassword(selected.email)}
                    disabled={updatingAuth}
                  >
                    <Key size={14} className="mr-1" /> Alterar Senha
                  </Button>

                  <Button 
                    variant="outline" 
                    size="sm" 
                    className="rounded-full text-xs h-8 border-primary text-primary hover:bg-primary/5"
                    onClick={() => resendStaffNotification(selected)}
                    disabled={resending}
                  >
                    <MessageSquare size={14} className="mr-1" /> 
                    {resending ? "A enviar..." : "Reenviar p/ Colaborador"}
                  </Button>
                </div>
                <div className="grid sm:grid-cols-2 gap-3 text-sm">
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-muted-foreground">Nome Completo</Label>
                    <Input 
                      value={selected.full_name} 
                      onChange={(e) => setSelected({...selected, full_name: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-[10px] uppercase text-muted-foreground">Colaborador(es) Atribuído(s)</Label>
                    <div className="flex flex-wrap gap-2 mb-2 min-h-[40px] p-2 border border-input rounded-md bg-background">
                      {(selected.assigned_staff || []).map((name) => (
                        <Badge key={name} variant="secondary" className="flex items-center gap-1 py-1">
                          {name}
                          <button 
                            type="button" 
                            onClick={() => {
                              const newList = (selected.assigned_staff || []).filter(n => n !== name);
                              setSelected({...selected, assigned_staff: newList});
                            }}
                            className="text-muted-foreground hover:text-foreground"
                          >
                            <Trash2 size={12} />
                          </button>
                        </Badge>
                      ))}
                      {(!selected.assigned_staff || selected.assigned_staff.length === 0) && (
                        <span className="text-xs text-muted-foreground italic">Nenhum colaborador atribuído</span>
                      )}
                    </div>
                    <div className="flex gap-2">
                      <Select 
                        onValueChange={(val) => {
                          if (val && !(selected.assigned_staff || []).includes(val)) {
                            const newList = [...(selected.assigned_staff || []), val];
                            setSelected({...selected, assigned_staff: newList});
                          }
                        }}
                      >
                        <SelectTrigger className="h-8 text-sm flex-1">
                          <SelectValue placeholder="Adicionar colaborador..." />
                        </SelectTrigger>
                        <SelectContent>
                          {staffList
                            .filter(s => !(selected.assigned_staff || []).includes(s.name))
                            .map(s => (
                              <SelectItem key={s.id} value={s.name}>{s.name}</SelectItem>
                            ))}
                        </SelectContent>
                      </Select>
                      <Input 
                        placeholder="Outro..." 
                        className="h-8 text-sm flex-1"
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const val = e.currentTarget.value.trim();
                            if (val && !(selected.assigned_staff || []).includes(val)) {
                              setSelected({...selected, assigned_staff: [...(selected.assigned_staff || []), val]});
                              e.currentTarget.value = '';
                            }
                          }
                        }}
                      />
                    </div>
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-muted-foreground">Email</Label>
                    <Input 
                      value={selected.email} 
                      onChange={(e) => setSelected({...selected, email: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-muted-foreground">Telemóvel</Label>
                    <Input 
                      value={selected.phone} 
                      onChange={(e) => setSelected({...selected, phone: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-muted-foreground">Data de Nascimento</Label>
                    <Input 
                      type="date"
                      value={selected.birth_date || ""} 
                      onChange={(e) => setSelected({...selected, birth_date: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1 sm:col-span-2">
                    <Label className="text-[10px] uppercase text-muted-foreground">Morada</Label>
                    <Input 
                      value={selected.address || ""} 
                      onChange={(e) => setSelected({...selected, address: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-muted-foreground">Profissão</Label>
                    <Input 
                      value={selected.profession || ""} 
                      onChange={(e) => setSelected({...selected, profession: e.target.value})}
                      className="h-8 text-sm"
                    />
                  </div>
                  <div className="space-y-1">
                    <Label className="text-[10px] uppercase text-muted-foreground">Contacto de Emergência</Label>
                    <Input 
                      value={`${selected.emergency_name || ""} ${selected.emergency_phone || ""}`.trim()} 
                      onChange={(e) => {
                        const val = e.target.value;
                        const parts = val.split(" ");
                        setSelected({
                          ...selected, 
                          emergency_name: parts.slice(0, -1).join(" ") || parts[0], 
                          emergency_phone: parts.length > 1 ? parts[parts.length-1] : ""
                        });
                      }}
                      className="h-8 text-sm"
                    />
                  </div>
                </div>

                <div className="rounded-xl border border-border p-4 space-y-2 text-sm">
                  <p className="font-semibold">Saúde</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(selected.health_conditions || []).length > 0 ? (
                      (selected.health_conditions || []).map((c) => <Badge key={c} variant="outline">{c}</Badge>)
                    ) : (
                      <span className="text-muted-foreground text-xs">Sem condições assinaladas</span>
                    )}
                    {selected.is_pregnant && <Badge>Grávida {selected.pregnancy_weeks ? `(${selected.pregnancy_weeks} sem.)` : ""}</Badge>}
                  </div>
                  {selected.allergies && <p><span className="text-muted-foreground">Alergias:</span> {selected.allergies}</p>}
                  {selected.oil_allergies && <p><span className="text-muted-foreground">Alergias a óleos/aromas:</span> {selected.oil_allergies}</p>}
                  {selected.medications && <p><span className="text-muted-foreground">Medicação:</span> {selected.medications}</p>}
                  {selected.surgeries && <p><span className="text-muted-foreground">Cirurgias/lesões:</span> {selected.surgeries}</p>}
                  {selected.goals && <p><span className="text-muted-foreground">Objetivos:</span> {selected.goals}</p>}
                  {selected.observations && <p><span className="text-muted-foreground">Observações:</span> {selected.observations}</p>}
                  {Object.entries(((selected as any).custom_fields || {}) as Record<string, any>).map(([k, v]) => {
                    const isPasswordKey = k.toLowerCase().includes("password") || k.toLowerCase().includes("palavra-passe") || k.toLowerCase().includes("senha");
                    return (
                      <p key={k}>
                        <span className="text-muted-foreground">{k}:</span>{" "}
                        {isPasswordKey ? "******" : (Array.isArray(v) ? v.join(", ") : typeof v === "boolean" ? (v ? "Sim" : "Não") : String(v))}
                      </p>
                    );
                  })}
                </div>


                <div className="rounded-xl border border-border p-4 space-y-2 text-sm">
                  <p className="font-semibold">Preferências & aromaterapia</p>
                  <div className="flex flex-wrap gap-1.5">
                    {(selected.treatment_types || []).map((t) => <Badge key={t} variant="outline">{t}</Badge>)}
                  </div>
                  {selected.pressure_preference && <p><span className="text-muted-foreground">Pressão desejada:</span> {selected.pressure_preference}</p>}
                  {selected.avoid_areas && <p><span className="text-muted-foreground">Zonas a evitar:</span> {selected.avoid_areas}</p>}
                  <p>
                    <span className="text-muted-foreground">Gosta de óleos essenciais:</span> {selected.likes_essential_oils ? "Sim" : "Não"}
                    {" · "}
                    <span className="text-muted-foreground">Autoriza aromas:</span> {selected.aroma_consent ? "Sim" : "Não"}
                  </p>
                  {selected.aroma_consent && selected.aroma_preference && (
                    <p><span className="text-muted-foreground">Aroma preferido:</span> {selected.aroma_preference}</p>
                  )}
                </div>


                {selected.signature_data && (
                  <div className="rounded-xl border border-border p-4">
                    <p className="text-sm font-semibold mb-2">Assinatura digital</p>
                    <img src={selected.signature_data} alt="Assinatura" className="max-h-32 bg-white rounded-lg border border-border" />
                    {selected.signed_at && (
                      <p className="text-[11px] text-muted-foreground mt-2">
                        Assinada em {new Date(selected.signed_at).toLocaleString("pt-PT")}
                      </p>
                    )}
                  </div>
                )}

                <div className="space-y-6 pt-2">
                  <div className="space-y-3">
                    <div className="flex items-center justify-between border-b border-border pb-2">
                      <p className="font-semibold text-sm flex items-center gap-2">
                        <History size={15} className="text-primary" /> 
                        Histórico de Reservas ({clientBookings.length})
                      </p>
                    </div>
                    
                    {loadingHistory ? (
                      <div className="py-4 text-center text-xs text-muted-foreground animate-pulse">A carregar reservas...</div>
                    ) : clientBookings.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-2 italic text-center">Nenhuma reserva encontrada para este cliente.</p>
                    ) : (
                      <div className="grid gap-2">
                        {clientBookings.map((b) => (
                          <div key={b.id} className="rounded-xl border border-border bg-accent/5 p-3 text-sm transition-all hover:bg-accent/10">
                            <div className="flex justify-between items-start mb-2">
                              <div>
                                <p className="font-bold text-foreground">{(b.services as any)?.name || "Serviço"}</p>
                                <div className="flex items-center gap-3 mt-1">
                                  <span className="text-[10px] flex items-center gap-1 text-muted-foreground">
                                    <CalendarDays size={10} /> {new Date(b.booking_date).toLocaleDateString("pt-PT")}
                                  </span>
                                  <span className="text-[10px] flex items-center gap-1 text-muted-foreground">
                                    <Clock size={10} /> {b.booking_time.slice(0, 5)}
                                  </span>
                                </div>
                              </div>
                              <Badge variant="outline" className="text-[9px] uppercase font-bold tracking-wider">
                                {b.status === "completed" ? "Concluída" : 
                                 b.status === "confirmed" ? "Confirmada" : 
                                 b.status === "pending" ? "Pendente" : b.status}
                              </Badge>
                            </div>
                            
                            <div className="flex items-center justify-between border-t border-border/50 pt-2 mt-2">
                              <div className="flex items-center gap-2">
                                <CreditCard size={12} className="text-muted-foreground" />
                                <span className="font-medium text-primary">€{Number(b.total_price).toFixed(2)}</span>
                                {Number(b.discount_amount || 0) > 0 && (
                                  <div className="flex items-center gap-1 text-[10px] text-destructive bg-destructive/10 px-1.5 py-0.5 rounded-full font-bold">
                                    <Percent size={8} /> -€{Number(b.discount_amount).toFixed(2)}
                                  </div>
                                )}
                              </div>
                              <span className="text-[10px] text-muted-foreground flex items-center gap-1">
                                <MapPin size={10} /> {b.location_type === "home" ? "Domicílio" : "Estúdio"}
                              </span>
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <div className="flex items-center justify-between border-b border-border pb-2">
                      <p className="font-semibold text-sm flex items-center gap-2">
                        <Ruler size={15} className="text-primary" /> 
                        Avaliações & Medidas ({sessions.length})
                      </p>
                      <Button size="sm" variant="ghost" className="h-7 text-[10px] gap-1 px-2 border border-primary/20 hover:bg-primary/5 rounded-full" onClick={openNewSession}>
                        <Plus size={12} /> Nova avaliação
                      </Button>
                    </div>

                    {loadingHistory ? (
                      <div className="py-4 text-center text-xs text-muted-foreground animate-pulse">A carregar histórico...</div>
                    ) : sessions.length === 0 ? (
                      <p className="text-xs text-muted-foreground py-2 italic text-center">Ainda sem avaliações registadas.</p>
                    ) : (
                      <div className="space-y-2">
                        {sessions.map((s) => (
                          <div key={s.id} className="rounded-xl border border-border p-3 text-sm space-y-1 relative group">
                            <div className="flex items-center justify-between gap-2">
                              <p className="font-medium">
                                {new Date(s.session_date).toLocaleDateString("pt-PT")}
                                {s.service_performed ? `, ${s.service_performed}` : ""}
                              </p>
                              <Button variant="ghost" size="icon" className="h-7 w-7 opacity-0 group-hover:opacity-100 transition-opacity" onClick={() => deleteSession(s.id)}>
                                <Trash2 size={14} className="text-destructive/70" />
                              </Button>
                            </div>
                            {s.staff_name && <p className="text-xs text-muted-foreground">Profissional: {s.staff_name}</p>}
                            {s.products_used && <p className="text-xs italic text-muted-foreground/80">Produtos: {s.products_used}</p>}
                            {s.notes && <p className="text-xs text-muted-foreground bg-muted/30 p-2 rounded-lg mt-1">{s.notes}</p>}
                            <div className="flex flex-wrap gap-1.5 pt-1">
                              {s.weight && <Badge variant="secondary" className="text-[10px]">{s.weight} kg</Badge>}
                              {s.height && <Badge variant="secondary" className="text-[10px]">{s.height} cm</Badge>}
                              {Object.entries(s.measurements || {}).map(([k, v]) => (
                                <Badge key={k} variant="outline" className="text-[10px] bg-background">
                                  {MEASURE_FIELDS.find((m) => m.key === k)?.label.replace(" (cm)", "") || k}: {v} cm
                                </Badge>
                              ))}
                            </div>
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                </div>

                <div className="pt-4 border-t border-border">
                  <Button variant="ghost" className="text-destructive w-full hover:bg-destructive/5 hover:text-destructive" onClick={() => deleteRecord(selected.id)}>
                    <Trash2 size={15} className="mr-2" /> Apagar ficha de cliente
                  </Button>
                </div>
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* New session dialog */}
      <Dialog open={sessionOpen} onOpenChange={setSessionOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Registar sessão</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid sm:grid-cols-2 gap-4">
              <div className="space-y-2">
                <Label>Data</Label>
                <Input type="date" value={sessionForm.session_date} onChange={(e) => setSessionForm({ ...sessionForm, session_date: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Serviço realizado</Label>
                <Input value={sessionForm.service_performed} onChange={(e) => setSessionForm({ ...sessionForm, service_performed: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Peso (kg)</Label>
                <Input type="number" step="0.1" value={sessionForm.weight} onChange={(e) => setSessionForm({ ...sessionForm, weight: e.target.value })} />
              </div>
              <div className="space-y-2">
                <Label>Altura (cm)</Label>
                <Input type="number" step="0.1" value={sessionForm.height} onChange={(e) => setSessionForm({ ...sessionForm, height: e.target.value })} />
              </div>
            </div>

            <div className="space-y-2">
              <Label>Produtos / óleos utilizados</Label>
              <Input value={sessionForm.products_used} onChange={(e) => setSessionForm({ ...sessionForm, products_used: e.target.value })} />
            </div>

            <div className="space-y-2">
              <Label className="flex items-center gap-2"><Ruler size={14} /> Medidas corporais</Label>
              <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                {MEASURE_FIELDS.map((m) => (
                  <div key={m.key} className="space-y-1">
                    <span className="text-[11px] text-muted-foreground">{m.label}</span>
                    <Input
                      type="number"
                      step="0.1"
                      value={measures[m.key] || ""}
                      onChange={(e) => setMeasures({ ...measures, [m.key]: e.target.value })}
                    />
                  </div>
                ))}
              </div>
            </div>

            <div className="space-y-2">
              <Label>Notas da sessão</Label>
              <Textarea rows={4} value={sessionForm.notes} onChange={(e) => setSessionForm({ ...sessionForm, notes: e.target.value })} />
            </div>

            <Button className="w-full rounded-full" disabled={savingSession} onClick={saveSession}>
              {savingSession ? "A guardar..." : "Guardar sessão"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ClientsAdmin;
